import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Button, Center, Group, Stack, Text, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { Checkbox } from '@mui/material'
import { IconCheck, IconChecks, IconLineDashed, IconX } from '@tabler/icons-react'
import { ColDef, ICellRendererParams, IGetRowsParams, RowClickedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { AxiosError } from 'axios'
import { Link as RALink, useGetList, useNotify } from 'react-admin'
import { useTranslate } from 'react-admin'
import { YoutubeVideosAttributesApiDTO } from '../../../codegen/generated/Api.schemas'
import { getApiVideoPromotionVideos } from '../../../codegen/generated/video-promotion'
import { AgGridHelper } from '../../../helpers/AgGrid.helper'
import { DateHelper } from '../../../helpers/Date.helper'
import { DEFAULT_PAGE_SIZE } from '../../../helpers/constants'
import { useAgGridBulkSelect } from '../../../pages/Imports/AgGrid/aggridBulkSelect'
import { AgGridLoader, useAgGridLoader } from '../../../pages/Imports/AgGrid/aggridLoader'
import { useVideoPromotionStore } from '../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../types/YoutubeChannel'
import { LXPageSize } from '../../LXPageSize/LXPageSize'
import { NewPromotionVideosSearch } from './NewPromotionVideosSearch'
import classes from './NewPromotionVideosSelect.module.scss'

const ROW_HEIGHT = 60
const MIN_GRID_HEIGHT = 400
const GRID_BOTTOM_GAP = 48
const SELECT_ALL_MAX = 2000
const SELECT_ALL_PAGE_SIZE = 200

export const NewPromotionVideosSelect = () => {
  const t = useTranslate()
  const notify = useNotify()
  const gridRef = useRef<AgGridReact>(null)
  const [matchingTotal, setMatchingTotal] = useState(0)
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const { startLoading, stopLoading } = useAgGridLoader(gridRef)
  const {
    currentPageHasEverythingSelected,
    onCheckAllChanged,
    onSelectionChanged,
    onPaginationChanged,
  } = useAgGridBulkSelect(gridRef)
  const videoPromotionStore = useVideoPromotionStore()
  const { defaultColumnProperties } = AgGridHelper

  const gridContainerRef = useRef<HTMLDivElement>(null)
  const { height: viewportHeight } = useViewportSize()
  const [gridTop, setGridTop] = useState(0)
  // biome-ignore lint/correctness/useExhaustiveDependencies: the grid only renders once a channel is selected
  useLayoutEffect(() => {
    const container = gridContainerRef.current
    if (container) {
      setGridTop(container.getBoundingClientRect().top + window.scrollY)
    }
  }, [videoPromotionStore.youtubeChannelId])
  const gridHeight = Math.max(MIN_GRID_HEIGHT, viewportHeight - gridTop - GRID_BOTTOM_GAP)

  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        ...defaultColumnProperties,
        field: 'title',
        headerName: 'Video',
        checkboxSelection: true,
        minWidth: 450,
        headerComponent: () => {
          return (
            <>
              <Checkbox
                checked={currentPageHasEverythingSelected}
                onChange={onCheckAllChanged}
                style={{
                  paddingLeft: 0,
                }}
              />
              <div>Video</div>
            </>
          )
        },
        cellRenderer: (params: ICellRendererParams<YoutubeVideosAttributesApiDTO>) => {
          if (!params.data) {
            return null
          }

          return (
            <Stack gap={0} justify="center" h="100%">
              <Text
                size="sm"
                fw={600}
                lh={1.3}
                lineClamp={2}
                title={params.data.title}
                style={{ whiteSpace: 'normal' }}
              >
                {params.data.title}
              </Text>
            </Stack>
          )
        },
      },
      {
        ...defaultColumnProperties,
        field: 'youtubeChannelId',
        headerName: t('_generics.youtubeChannel'),
        cellRenderer: (params: ICellRendererParams<YoutubeVideosAttributesApiDTO>) => {
          if (!params.data) {
            return null
          }
          if (params.data?.youtubeChannelId) {
            const youtubeChannel = (youtubeChannelsQuery?.data ?? []).find(
              yc => yc.id === params.data?.youtubeChannelId,
            )

            return (
              <RALink
                to={`/youtubeChannels/${params.data.youtubeChannelId}`}
                target="_blank"
                rel="noreferrer"
              >
                {youtubeChannel?.name ?? params.data.youtubeChannelId}
              </RALink>
            )
          }
        },
      },
      {
        ...defaultColumnProperties,
        field: 'viewsCount',
        headerName: t('_generics.views'),
        width: 200,
        cellRenderer: (params: ICellRendererParams<YoutubeVideosAttributesApiDTO>) => {
          if (!params.data?.viewsCount) {
            return (
              <Text size="sm" fs="italic" c="dimmed" opacity={0.6} lh="inherit">
                {t('_generics.undefined')}
              </Text>
            )
          }

          return params.data?.viewsCount
        },
      },
      {
        ...defaultColumnProperties,
        field: 'hasEndscreen',
        headerName: t('pages.videoPromotion.hasEndscreen'),
        width: 200,
        cellRenderer: (params: ICellRendererParams<YoutubeVideosAttributesApiDTO>) => {
          if (!params.data) {
            return (
              <Text size="sm" fs="italic" c="dimmed" opacity={0.6} lh="inherit">
                {t('_generics.undefined')}
              </Text>
            )
          }

          return params.data?.hasEndscreen ? (
            <ThemeIcon c="green" variant="transparent" style={{ verticalAlign: 'middle' }}>
              <IconCheck size={22} />
            </ThemeIcon>
          ) : (
            <IconLineDashed size={18} color="grey" style={{ verticalAlign: 'middle' }} />
          )
        },
      },
      {
        ...defaultColumnProperties,
        field: 'publishedOnYoutubeAt',
        headerName: t('_generics.publishedAt'),
        cellRenderer: (params: ICellRendererParams<YoutubeVideosAttributesApiDTO>) => {
          if (!params.data?.publishedOnYoutubeAt) {
            return (
              <Text size="sm" fs="italic" c="dimmed" opacity={0.6} lh="inherit">
                {t('_generics.undefined')}
              </Text>
            )
          }

          return DateHelper.format(params.data.publishedOnYoutubeAt)
        },
      },
    ],
    [
      t,
      currentPageHasEverythingSelected,
      onCheckAllChanged,
      defaultColumnProperties,
      youtubeChannelsQuery?.data,
    ],
  )

  const getRows = useCallback(
    async (params: IGetRowsParams) => {
      const pageSize = params.endRow - params.startRow
      startLoading(`${params.startRow}`)
      const data = await getApiVideoPromotionVideos({
        ...videoPromotionStore.selectionVideoStaticFilters,
        youtubeChannelId: videoPromotionStore?.youtubeChannelId ?? '',
        page: params.startRow / pageSize + 1,
        pageSize,
      })
      params.successCallback(data.body.videos, data.body.total)
      setMatchingTotal(data.body.total)
      const selectedIds = new Set(useVideoPromotionStore.getState().youtubeVideoIds)
      gridRef.current?.api.forEachNode(node => {
        if (node.data && selectedIds.has(node.data.id) && !node.isSelected()) {
          node.setSelected(true)
        }
      })
      stopLoading(`${params.startRow}`)
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: a finished sync reloads the grid
    [
      videoPromotionStore.selectionVideoStaticFilters,
      videoPromotionStore?.youtubeChannelId,
      videoPromotionStore.videosSyncedAt,
      startLoading,
      stopLoading,
    ],
  )

  const datasource = useMemo(() => {
    return { getRows }
  }, [getRows])

  const onGridSelectionChanged = useCallback(() => {
    onSelectionChanged()
    const loadedVideos: YoutubeVideosAttributesApiDTO[] = []
    const selectedIds = new Set<YoutubeVideosAttributesApiDTO['id']>()
    gridRef.current?.api.forEachNode(node => {
      if (!node.data) {
        return
      }
      loadedVideos.push(node.data)
      if (node.isSelected()) {
        selectedIds.add(node.data.id)
      }
    })
    useVideoPromotionStore.getState().updateVideosSelection(loadedVideos, selectedIds)
  }, [onSelectionChanged])

  const onRowClicked = useCallback((event: RowClickedEvent) => {
    const target = event.event?.target
    if (target instanceof Element && target.closest('a, .ag-selection-checkbox')) {
      return
    }
    event.node.setSelected(!event.node.isSelected())
  }, [])

  const selectAllMatching = useCallback(async () => {
    const { youtubeChannelId, selectionVideoStaticFilters, selectVideos } =
      useVideoPromotionStore.getState()
    if (!youtubeChannelId) {
      return
    }
    setIsSelectingAll(true)
    try {
      const videos: YoutubeVideosAttributesApiDTO[] = []
      for (let page = 1; ; page++) {
        const { body } = await getApiVideoPromotionVideos({
          ...selectionVideoStaticFilters,
          youtubeChannelId,
          page,
          pageSize: SELECT_ALL_PAGE_SIZE,
        })
        videos.push(...body.videos)
        if (body.videos.length === 0 || videos.length >= body.total) {
          break
        }
      }
      selectVideos(videos)
      gridRef.current?.api.forEachNode(node => {
        if (node.data && !node.isSelected()) {
          node.setSelected(true)
        }
      })
    } catch (e: unknown) {
      const message =
        (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e
      notify(message, { type: 'error' })
    } finally {
      setIsSelectingAll(false)
    }
  }, [notify])

  const onSelectAllMatching = useCallback(() => {
    modals.openConfirmModal({
      title: (
        <Text fw="bold">
          {t('pages.videoPromotion.selectAllMatching', { smart_count: matchingTotal })}
        </Text>
      ),
      children: (
        <Text size="sm">
          {t('pages.videoPromotion.selectAllMatchingConfirm', { smart_count: matchingTotal })}
        </Text>
      ),
      labels: { confirm: t('_generics.confirm'), cancel: t('_generics.cancel') },
      cancelProps: { variant: 'subtle' },
      onConfirm: selectAllMatching,
    })
  }, [matchingTotal, t, selectAllMatching])

  const onDeselectAll = useCallback(() => {
    videoPromotionStore.clearVideosSelection()
    gridRef.current?.api.deselectAll()
  }, [videoPromotionStore])

  const selectedCount = videoPromotionStore.youtubeVideoIds.length

  return (
    <>
      <NewPromotionVideosSearch />
      {videoPromotionStore?.youtubeChannelId ? (
        <>
          <Group p="sm" h={50} justify="space-between" bg={selectedCount > 0 ? 'blue.1' : 'gray.0'}>
            <Group>
              <Text size="sm" c={selectedCount > 0 ? undefined : 'dimmed'}>
                {selectedCount > 0
                  ? `${selectedCount} ${t('_generics.selected', { smart_count: selectedCount })}`
                  : t('pages.videoPromotion.noVideoSelected')}
              </Text>
              {selectedCount > 0 && (
                <Button
                  size="compact-sm"
                  variant="subtle"
                  leftSection={<IconX size={14} />}
                  onClick={onDeselectAll}
                >
                  {t('pages.videoPromotion.deselectAll')}
                </Button>
              )}
            </Group>
            {matchingTotal > 0 && (
              <Tooltip
                label={t('pages.videoPromotion.selectAllTooMany', {
                  smart_count: matchingTotal,
                  max: SELECT_ALL_MAX,
                })}
                disabled={matchingTotal <= SELECT_ALL_MAX}
              >
                <Button
                  size="compact-sm"
                  variant="light"
                  leftSection={<IconChecks size={14} />}
                  loading={isSelectingAll}
                  data-disabled={matchingTotal > SELECT_ALL_MAX || undefined}
                  onClick={matchingTotal > SELECT_ALL_MAX ? undefined : onSelectAllMatching}
                >
                  {t('pages.videoPromotion.selectAllMatching', { smart_count: matchingTotal })}
                </Button>
              </Tooltip>
            )}
          </Group>
          <div
            ref={gridContainerRef}
            className={`ag-theme-material ${classes.grid}`}
            style={
              {
                width: '100%',
                height: gridHeight,
                '--ag-row-height': `${ROW_HEIGHT}px`,
              } as CSSProperties
            }
          >
            <AgGridReact
              ref={gridRef}
              enableCellTextSelection
              ensureDomOrder
              rowModelType="infinite"
              rowHeight={ROW_HEIGHT}
              paginationPageSize={DEFAULT_PAGE_SIZE}
              cacheBlockSize={DEFAULT_PAGE_SIZE}
              datasource={datasource}
              pagination={true}
              defaultColDef={{
                cellStyle: { paddingLeft: 8, paddingRight: 8 },
              }}
              columnDefs={columnDefs}
              rowSelection="multiple"
              rowMultiSelectWithClick
              suppressRowClickSelection
              getRowId={params => params.data.id}
              onSelectionChanged={onGridSelectionChanged}
              onRowClicked={onRowClicked}
              onPaginationChanged={onPaginationChanged}
              loadingOverlayComponent={AgGridLoader}
            />
            <LXPageSize
              /* biome-ignore lint/style/noNonNullAssertion: <explanation> */
              onPageSizeChange={size => gridRef.current?.api!.paginationSetPageSize(size)}
            />
          </div>
        </>
      ) : (
        <Center mt="xl">
          <Title order={5} c="dimmed">
            {t('pages.videoPromotion.selectYoutubeChannelToList')}
          </Title>
        </Center>
      )}
    </>
  )
}
